import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiArrowRight, FiPlay, FiClock } = FiIcons;

const Urgency = () => {
  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section className="py-20 bg-gradient-to-br from-gray-900 via-accent-magenta to-accent-purple text-white">
      <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="mb-12"
        >
          <div className="flex items-center justify-center gap-2 mb-6">
            <SafeIcon icon={FiClock} className="w-8 h-8 text-accent-gold" />
            <h2 className="text-3xl lg:text-5xl font-playfair font-bold">
              Your Recovery Can't Wait for Perfect Timing
            </h2>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          viewport={{ once: true }}
          className="space-y-6 mb-12"
        >
          <p className="text-xl leading-relaxed">
            Every day you fight this battle with weakened executive functions is another day your addiction has the advantage. The longer those neural pathways stay damaged, the harder recovery becomes.
          </p>

          <p className="text-xl leading-relaxed">
            But here's the hope: <strong>your brain is incredibly adaptable.</strong> The right executive function training can rebuild the cognitive skills that addiction has compromised.
          </p>

          <div className="bg-white/10 backdrop-blur-sm rounded-3xl p-8 my-8">
            <p className="text-2xl font-semibold mb-4">
              You didn't choose to have your brain hijacked by addiction.
            </p>
            <p className="text-xl">
              But you can choose to fight back with better tools.
            </p>
          </div>

          <p className="text-xl leading-relaxed">
            Your recovery deserves more than willpower and hope. <strong>It deserves actual skills.</strong>
          </p>

          <div className="space-y-4 text-lg">
            <p>Stop trying to recover with a brain that's been set up to fail.</p>
            <p>Stop believing that struggling every day is just "how recovery is."</p>
            <p>Stop accepting relapse as inevitable.</p>
          </div>

          <p className="text-2xl font-bold bg-gradient-to-r from-accent-gold to-accent-yellow bg-clip-text text-transparent">
            The cognitive control you're craving—the ability to consistently choose recovery—is learnable, trainable, and within your reach.
          </p>

          <p className="text-3xl font-playfair font-bold">
            Don't just resist your addiction. Outgrow it.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          viewport={{ once: true }}
          className="bg-white/10 backdrop-blur-sm rounded-3xl p-8 mb-12"
        >
          <p className="text-lg leading-relaxed">
            We're so confident in executive function therapy's power to transform recovery, we guarantee meaningful progress. Commit to your growth, do the work, and if you don't see real improvement in your recovery skills, we'll make it right.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.6 }}
          viewport={{ once: true }}
          className="flex flex-col sm:flex-row gap-6 justify-center"
        >
          <motion.button
            onClick={() => scrollToSection('contact')}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="bg-white text-accent-magenta px-8 py-4 rounded-full font-semibold text-lg flex items-center justify-center gap-2 hover:bg-gray-100 transition-colors duration-200"
          >
            Book Your Confidential Consultation (Free)
            <SafeIcon icon={FiArrowRight} className="w-5 h-5" />
          </motion.button>

          <motion.a
            href="https://www.wethebillions.com/quiz"
            target="_blank"
            rel="noopener noreferrer"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="border-2 border-white text-white px-8 py-4 rounded-full font-semibold text-lg flex items-center justify-center gap-2 hover:bg-white hover:text-accent-magenta transition-all duration-200"
          >
            Take the FREE Quiz
            <SafeIcon icon={FiPlay} className="w-5 h-5" />
          </motion.a>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.8 }}
          viewport={{ once: true }}
          className="mt-12 space-y-4"
        >
          <div className="grid md:grid-cols-3 gap-6 text-center">
            <div>
              <p className="font-semibold">📧 Email</p>
              <a
                href="mailto:hello@wethebillions.com"
                className="text-accent-gold hover:text-accent-yellow"
              >
                hello@wethebillions.com
              </a>
            </div>
            <div>
              <p className="font-semibold">📞 Call/Text</p>
              <a
                href="tel:+12026560054"
                className="text-accent-gold hover:text-accent-yellow"
              >
                (202) 656-0054
              </a>
            </div>
            <div>
              <p className="font-semibold">🗓️ Book Online</p>
              <a
                href="https://www.wethebillions.com"
                className="text-accent-gold hover:text-accent-yellow"
                target="_blank"
                rel="noopener noreferrer"
              >
                wethebillions.com
              </a>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Urgency;